import { useQuery } from '@tanstack/react-query';
import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { dynamicFieldService } from '@/services/core/DynamicFieldService';
import { authService } from '@/services/auth/authService';


interface EnhancedSchemas {
  schema: JsonSchema;
  uiSchema: UISchemaElement;
}

/**
 * Query key factory for enhanced schemas
 */
export const enhancedSchemaKeys = {
  all: (tenantId?: string | null) => ['enhancedSchema', tenantId] as const,
  schema: (
    tenantId?: string | null,
    entityName?: string,
    viewName?: string,
    orgUnitId?: string | null,
    base?: string,
  ) => [...enhancedSchemaKeys.all(tenantId), entityName, viewName, orgUnitId ?? null, base ?? null] as const,
};

/** Every distinct schema content seen, numbered; and each schema object's number, so it is read once. */
const contentIds = new Map<string, number>();
const objectIds = new WeakMap<object, number>();

/**
 * A short name for a base schema's content. The enhanced schema is built FROM the base schemas, so
 * they belong in its cache key: a detail view renders the create schemas for a new record and the
 * update schemas once it is saved — in the same mounted view, since saving navigates to the new
 * record in place — and without them in the key the saved record kept the create form's fields.
 */
function schemaId(value: object): number {
  let id = objectIds.get(value);
  if (id === undefined) {
    const json = JSON.stringify(value);
    id = contentIds.get(json) ?? contentIds.size + 1;
    contentIds.set(json, id);
    objectIds.set(value, id);
  }
  return id;
}

/**
 * Hook to enhance JsonForms schemas with UDFs
 * Uses TanStack Query to cache enhanced schemas per entity/view/orgUnit/base schema.
 * Automatically extracts OrgUnitId from entity context.
 */
export function useEnhancedJsonForms(
  entityName: string,
  viewName: string,
  baseSchema: JsonSchema,
  baseUiSchema: UISchemaElement,
  _entity?: any,
  _orgUnitId?: string | null
) {
  const tenantId = authService.getTenantId();

  // UDF scope is the active Legal Entity from the top menu
  const effectiveOrgUnitId = authService.getLegalEntityId();

  const { data, isLoading, error } = useQuery<EnhancedSchemas, Error>({
    queryKey: enhancedSchemaKeys.schema(
      tenantId, entityName, viewName, effectiveOrgUnitId, `${schemaId(baseSchema)}.${schemaId(baseUiSchema)}`,
    ),
    queryFn: async () => {
      // Use backend schema enhancement service (preferred)
      // Falls back to client-side enhancement if endpoint fails
      try {
        const enhancedJsonSchema = await dynamicFieldService.enhanceJsonSchema(
          baseSchema,
          entityName,
          viewName,
          effectiveOrgUnitId || undefined
        );

        const enhancedUi = await dynamicFieldService.enhanceUiSchema(
          baseUiSchema,
          entityName,
          viewName,
          effectiveOrgUnitId || undefined
        );

        return { schema: enhancedJsonSchema, uiSchema: enhancedUi };
      } catch (backendError) {
        // If backend enhancement fails, fall back to client-side enhancement
        console.warn('Backend schema enhancement failed, using client-side enhancement:', backendError);
      }

      // Fallback: Client-side enhancement by fetching fields
      const fields = await dynamicFieldService.getFieldsForEntity(
        entityName,
        effectiveOrgUnitId || undefined
      );

      const visibleFields = fields.filter(f => f.isActive !== false && f.isVisible !== false);

      if (visibleFields.length === 0) {
        return { schema: baseSchema, uiSchema: baseUiSchema };
      }

      // Enhance JSON Schema
      const enhancedJsonSchema = { ...baseSchema };
      if (!enhancedJsonSchema.properties) {
        enhancedJsonSchema.properties = {};
      }

      // Add UDF properties to schema
      visibleFields.forEach((field) => {
        const fieldSchema: any = {
          type: mapFieldTypeToJsonSchemaType(field.fieldType),
          title: field.displayName,
        };

        if (field.description) {
          fieldSchema.description = field.description;
        }

        // Apply type-specific config
        if (field.typeConfig) {
          applyTypeConfig(fieldSchema, field.typeConfig, field.fieldType);
        }

        enhancedJsonSchema.properties![field.fieldName] = fieldSchema;

        // Add to required array if needed
        if (field.validationRules?.required) {
          if (!enhancedJsonSchema.required) {
            enhancedJsonSchema.required = [];
          }
          if (!enhancedJsonSchema.required.includes(field.fieldName)) {
            enhancedJsonSchema.required.push(field.fieldName);
          }
        }
      });

      // Enhance UI Schema
      const enhancedUi = JSON.parse(JSON.stringify(baseUiSchema));
      if (!enhancedUi.elements) {
        enhancedUi.elements = [];
      }

      // Build UDF UI elements
      const sortedFields = [...visibleFields].sort((a, b) =>
        (a.displayOrder || 0) - (b.displayOrder || 0)
      );

      // Look for a customFields placeholder Group in the UI schema tree
      const placeholder = findCustomFieldsPlaceholder(enhancedUi);
      const defaultFieldWidth = placeholder?.options?.defaultFieldWidth;

      sortedFields.forEach((field) => {
        const uiElement: any = {
          type: 'Control',
          scope: `#/properties/${field.fieldName}`,
          options: {
            label: field.displayName,
          },
        };

        if (field.description) {
          uiElement.options.placeholder = field.description;
        }

        // Use field-specific width, fall back to placeholder's defaultFieldWidth
        if (field.width) {
          uiElement.options.width = field.width;
          uiElement.options.gridColumn = `span ${field.width}`;
        } else if (defaultFieldWidth) {
          uiElement.options.width = defaultFieldWidth;
          uiElement.options.gridColumn = `span ${defaultFieldWidth}`;
        }

        if (field.isReadOnly) {
          uiElement.options.readonly = true;
        }

        // Insert into placeholder Group or append at end
        if (placeholder) {
          placeholder.elements.push(uiElement);
        } else {
          enhancedUi.elements.push(uiElement);
        }
      });

      return { schema: enhancedJsonSchema, uiSchema: enhancedUi };
    },
  });

  return {
    enhancedSchema: data?.schema ?? baseSchema,
    enhancedUiSchema: data?.uiSchema ?? baseUiSchema,
    isLoading,
    error,
    orgUnitId: effectiveOrgUnitId
  };
}

/**
 * Map field type to JSON Schema type
 */
function mapFieldTypeToJsonSchemaType(fieldType: string): string {
  const typeMap: Record<string, string> = {
    String: 'string',
    Integer: 'integer',
    Decimal: 'number',
    Boolean: 'boolean',
    DateTime: 'string',
    Date: 'string',
    Enum: 'string',
    Reference: 'string',
  };

  return typeMap[fieldType] || 'string';
}

/**
 * Apply type-specific configuration to schema
 */
function applyTypeConfig(
  schema: any,
  typeConfig: Record<string, any>,
  fieldType: string
): void {
  if (typeConfig.maxLength !== undefined) {
    schema.maxLength = typeConfig.maxLength;
  }

  if (typeConfig.minLength !== undefined) {
    schema.minLength = typeConfig.minLength;
  }

  if (fieldType === 'Decimal' || fieldType === 'Integer') {
    if (typeConfig.min !== undefined) {
      schema.minimum = typeConfig.min;
    }
    if (typeConfig.max !== undefined) {
      schema.maximum = typeConfig.max;
    }
  }

  if (fieldType === 'DateTime') {
    schema.format = 'date-time';
  } else if (fieldType === 'Date') {
    schema.format = 'date';
  }
}

/**
 * Recursively find a Group element with options.customFields === true
 * in the UI schema tree. Returns the element so UDF controls can be injected into it.
 */
function findCustomFieldsPlaceholder(uiSchema: any): any | null {
  if (!uiSchema) return null;

  // Check if this element is the placeholder
  if (uiSchema.type === 'Group' && uiSchema.options?.customFields === true) {
    return uiSchema;
  }

  // Recurse into elements array
  if (Array.isArray(uiSchema.elements)) {
    for (const child of uiSchema.elements) {
      const found = findCustomFieldsPlaceholder(child);
      if (found) return found;
    }
  }

  return null;
}
